import { $, _, Backbone, Lethargy } from "js/vendor";
import { ds } from "js/core/models/dataService";
import log from "js/core/logger";
import { app } from "js/namespaces";
import * as geom from "js/core/utilities/geom";
import * as browser from "js/core/utilities/browser";
import React from "react";
import { controls } from "js/editor/ui";
import { SpeakerNotes } from "js/editor/dialogs/speakerNotes";
import { AnimationDialog } from "js/editor/dialogs/animationDialog";
import { EditorAnimationPanel } from "js/editor/editorPanels/EditorAnimationPanel";
import { CanvasEventType, CanvasRenderer, SlideCanvas } from "js/core/canvas";
import { isArrowKey, Key } from "js/core/utilities/keys";
import { ASSET_MAX_SIZE, DirectionType, SlideLockType } from "common/constants";
import CollaborationView from "js/editor/components/collaborationView";
import SelectionLayer from "js/editor/selectionLayer";
import { TextElement } from "js/elements/base/TextElement";
import { themeColors } from "js/react/sharedStyles";
import UndoManager from "js/editor/undoManager";
import Variations from "js/core/slide_templates/variations";
import { slideTemplates } from "js/core/slide_templates/slide_template";
import { SlideOptions } from "js/editor/slideOptions";
import { handleEditSharedSlide } from "js/editor/editShareSlide";
import { EditorMenu, getSlideMenu } from "js/editor/editorMenu";
import { EditorMenuBar } from "js/editor/editorMenuBar";
import VariationsPanel from "js/editor/editorPanels/VariationsPanel";
import { CollectionItemElement } from "js/elements/base/CollectionElement";
import { SlideGrid } from "js/editor/slideGrid";
import PageView from "js/editor/pageView";
import ErrorHandler from "js/core/utilities/errorHandler";
import { handlePasteImage } from "js/core/utilities/handlePasteImage";
import { trackActivity } from "js/core/utilities/utilities";
import renderReactRoot, { reactMount } from "js/react/renderReactRoot";
import PresentationSettingsContainer from "js/react/views/PresentationSettings/PresentationSettingsContainer";
import { setGlobal } from "reactn";
import { EditorCommentsPane } from "js/react/views/CommentsPane/EditorCommentsPane";
import { FeatureType } from "js/core/models/features";
import { CollaborationBarContainer } from "js/editor/collaborationBar";
import { ShowDialog, ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import { UIController } from "js/editor/dialogs/UIController";
import { AddSlideContainer } from "js/react/views/AddSlide";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import AddContentPanel from "js/editor/editorPanels/AddContentPanel";
import { AddAnnotationPanel } from "js/editor/editorPanels/AddAnnotationPanel";
import { UpdateTemplateVersionDialog } from "js/editor/dialogs/UpdateTemplateVersionDialog";
import { EditorLock } from "../core/models/editorLock";
import { SlideDemoMenu } from "js/editor/slideDemoMenu";
import { EditSharedSlideDialog } from "js/editor/TeamResources/TeamSlideDialog";

export const EditorView = PageView.extend({
    CANVAS_WIDTH: 1280,
    CANVAS_HEIGHT: 720,

    MENU_HEIGHT: 40,
    CANVAS_TOP: 70,
    CANVAS_BOTTOM: 30,
    CANVAS_PADDING: 140,

    MENU_BAR_WIDTH: 70,
    USER_MENU_BAR_WIDTH: 60,

    COMMENTS_BAR_WIDTH: 250,

    MINIMUM_CONTAINER_WIDTH: 600,
    MINIMUM_CONTAINER_HEIGHT: 400,

    //SIDE_PANEL_WIDTH: 700,
    SIDE_PANEL_HEIGHT: 300,
    MINIMUM_SLIDE_SCALE: 0.5,
    MAXIMUM_SLIDE_SCALE: 1,
    MINIMUM_SLIDE_OPACITY: 0.2,
    SLIDE_GAP: 100,

    UI_ANIMATION_DURATION: 350,

    id: "editor",
    menu: EditorMenu,

    ignoreArrowKeys: false,

    initialize: function({
        disableCollaborationLocks = false,
        hideSlideOptionsControls = false,
        hideCollaborationBar = false,
        disableRouterActions = false,
        menuBarOptions = {}
    } = {}) {
        const presentation = ds.selection.presentation;
        trackActivity("Presentation", "Open", presentation.id, null, {}, { audit: true });
        if (presentation.get("userId") !== app.user.id) {
            trackActivity("Collab", "EditPresentation", presentation.id, null, {}, { audit: true });
        }

        this.loadError = null;
        this.spellcheckers = [];

        this.isPresentationSettingsOpen = false;
        this.isCommentsOpen = false;
        this.hideSideSlides = false;

        this.disableCollaborationLocks = disableCollaborationLocks;
        this.hideSlideOptionsControls = hideSlideOptionsControls;
        this.hideCollaborationBar = hideCollaborationBar;
        this.disableRouterActions = disableRouterActions;
        this.menuBarOptions = menuBarOptions;

        this.lockModel = new EditorLock();

        this.menuHeight = 50;

        this.slides = [];

        if (ds.selection.presentation.get("isSlideEditor")) {
            this.isSlideEditor = true;
            this.CANVAS_PADDING = 40;
            this.CANVAS_TOP = 20;
            this.USER_MENU_BAR_WIDTH = 0;
            this.menuHeight = 0;
        }

        if (ds.selection.presentation.get("isSlideDemo")) {
            this.isSlideDemo = true;
            this.menu = null;
            this.CANVAS_TOP = 20;
            this.USER_MENU_BAR_WIDTH = 0;
            this.menuHeight = 0;
        }

        if (ds.selection.presentation.get("isTemplate")) {
            this.isTemplateEditor = true;
            this.USER_MENU_BAR_WIDTH = 0;
        }

        app.undoManager = new UndoManager();

        this.canvasRenderer = new CanvasRenderer({
            showLoader: false,
            showDelayedLoader: true
        });
        this.collaborators = new CollaborationView();

        setGlobal({
            accountAvatarUrl: app.user.getAvatarUrl()
        });

        // listen for ds.selection.slide to change and scroll to the new selected slide
        this.listenTo(ds.selection, "change:slide", function(model, slide, options) {
            if (slide) {
                // hide any sidebar except for speaker notes and animations
                if (this.isShowingSideBar && !this.isShowingSpeakerNotes && !this.isShowingAnimationDialog) {
                    this.hideSideBar();
                }

                if (this.currentCanvas) {
                    this.currentCanvas.removeAsCurrentCanvas();
                    this.currentCanvas = null;
                }
                this.layout(!options.skipScroll).then(() => {
                    this.setCurrentCanvas();
                });
            }
        });

        this.listenTo(ds.selection.presentation, "slideorder", () => {
            this._sortSlides();
            if (ds.selection.slide) {
                this.layout(true);
            }
        });

        // listen for changes to the current presentation name or theme
        this.listenTo(ds.selection.presentation, "change", (presentation, options) => {
            if (options && options.internalChange) {
                return;
            }

            if (presentation.hasChanged("name")) {
                $(".presentation_name").text(presentation.get("name"));
            }
            //update the themes
            if (app.themeManager.themeChanged(presentation)) {
                this.onThemeChanged(presentation);
            }
        });

        // listen for changes to the current presentation name or theme
        this.listenTo(ds.selection.presentation, "sharedThemeUpdated", presentation => {
            this.onThemeChanged(presentation);
        });

        $(window).on("resize.editor", () => {
            this.resize(false);
            if (this.sideBar) {
                this.resizeSideBar();
            }
        });

        $(window).on("keydown.editor", _.throttle(event => this.onKeyDown(event), 50));
        $(window).on("keypress.editor", _.throttle(event => this.onKeyPress(event), 50));
        $(window).on("paste.editor", event => this.handlePaste(event));

        window.addEventListener("beforeunload", () => {
            if (ds.selection.slide) {
                ds.selection.slide.finishedEditing();
            }
        });

        let onScroll = _.throttle(scroll => {
            if (scroll === 1) {
                this._goPrevSlide();
            } else if (scroll === -1) {
                this._goNextSlide();
            }
        }, 500, { trailing: false });

        let lethargy = new Lethargy();
        $(window).on("wheel.editor", event => {
            // Ignore mouse wheel over the comments sidebar
            if ([...window.document.querySelectorAll(".comments_container")].some(el => el === event.target || el.contains(event.target))) return;
            // Ignore mouse wheel over the sidebar
            if (
                this.sideBar &&
                !this.sideBar.preventCanvasEditingWhenOpen &&
                (
                    this.sideBar.$el[0].contains(event.target) ||
                    this.sideBar.$el[0] === event.target
                )
            ) return;
            // Ignore mouse wheel when the sidebar prevents canvas editing
            if (this.sideBar && this.sideBar.preventCanvasEditingWhenOpen) return;
            if (!this.isShowingSlides) return;
            if (this.isPresenting) return;
            // Checking if there are dialogs open (excluding the side bar)
            if (app.dialogManager.openDialogs.some(dialog => dialog !== this.sideBar)) return;
            if (this.slideGrid) return;
            if ($(".dropdown_menu").length) return;
            if ($(".MuiPopover-root").length) return;

            // Do not call preventDefault in versions 56 or higher in Chrome, it is now by default a "passive event"
            // and calling preventDefault will cause the error logs to fill with spam.
            if (!browser.isChrome || (browser.isChrome && browser.getChromeVersion() < 56)) {
                event.preventDefault();
            }
            event.stopPropagation();

            let direction = lethargy.check(event);
            if (direction !== false) {
                onScroll(direction);
            }
        });

        $(document).on("contextmenu.editor", event => {
            //when dialogs such as AddAssetsContainer are visible we can ignore preventDefault and stopPropagation
            if ($("body").find(".dialog-container").length) return;

            // don't prevent contextMenu on contentEditables
            if ($(event.target).closest("[contenteditable = true]").length) return;

            if (this.isPresenting) return;

            if (app.isEditingText) return;

            event.preventDefault();
            event.stopPropagation();
            this.showContextMenu(event);
        });

        this.listenTo(this.lockModel, "change:lockState", model => {
            //if any of the locks are on then the editor is not editable
            const editable = !Object.values(model.attributes.lockState).includes(true);

            if (ds.selection.slide.has("libraryItemId") && !model.attributes.lockState[SlideLockType.ORG]) {
                $(".locked-slide, .slide_info").hide();
                $(".save-slide-changes").show();
            }

            if (model.checkIfOnlyLockActive(SlideLockType.OBSOLETE_TEMPLATE)) {
                //hide any existing locks so only the template update lock is in view
                $(".locked-slide, .slide_info, .save-slide-changes").hide();
                app.mainView.currentMenu.showSlideLock("Template needs update", () => {
                    ShowDialog(UpdateTemplateVersionDialog, {
                        canvas: app.currentCanvas,
                        onUpdateTemplate: () => {
                            this.removeLockedObsoleteTemplateShield();
                        }
                    });
                });
            } else {
                app.mainView.currentMenu.hideSlideLock();
            }

            this.setEditorLockState(editable);
        });

        this.throttledResize = _.throttle(() => this.resize(false), 33);
    },

    async onThemeChanged(presentation) {
        const progressDialog = ShowDialog(ProgressDialog, {
            title: "Applying theme..."
        });

        const failedSlideIndexes = [];

        const refreshSlides = forceRender => Promise.all(this.slides.map((slide, slideIndex) => {
            if (!slide.canvas || !slide.canvas.layouter) {
                return;
            }

            slide.canvas.layouter.canvasElement.markStylesAsDirty();
            return slide.canvas.refreshCanvas({ forceRender })
                .catch(err => {
                    if (!forceRender) {
                        failedSlideIndexes.push(slideIndex);
                    }
                    throw err;
                });
        }));

        try {
            await app.themeManager.loadTheme(presentation);

            // Legacy code, I'm not sure if this is possible, but kept it just in case
            if (!this.slides) {
                progressDialog.props.closeDialog();
                return;
            }

            await refreshSlides(false);
        } catch (err) {
            if (failedSlideIndexes === 0) {
                log.error(err);
            }

            if (failedSlideIndexes.length > 0) {
                ShowWarningDialog({
                    title: "Some slides didn't fit",
                    message: `The content on slide${failedSlideIndexes.length > 1 ? "s" : ""} ${failedSlideIndexes.map(index => `#${index + 1}`).join(", ")} didn't fit with the new theme. 
                    We forced ${failedSlideIndexes.length > 1 ? "them" : "it"} to display but we recommend reverting the changes or removing some elements from the slide${failedSlideIndexes.length > 1 ? "s" : ""}
                    in order to allow it to display correctly.`
                });

                await refreshSlides(true);

                // Note: in future we should probably implement logic to revert theme changes
                // which will require refactoring the way the theme editor applies changes to presentation
                // (currently the changes are saved by the theme editor but then asynchronously get applied to the
                // slides by the editor upon a presentation model change event so at this point we don't know the actual
                // source of changes and where to revert them to in case canvases failed to refresh, this process has to be
                // streamlined/synchronized in order to allow reliable revertion logic)
            } else {
                ShowWarningDialog({
                    title: "Sorry, we couldn't make this change",
                    message: "Something went wrong, please email support@beautiful.ai for assistance."
                });
            }
        } finally {
            progressDialog.props.closeDialog();
        }
    },

    setEditorLockState(editable, isLibrarySlide) {
        if (isLibrarySlide) {
            return true;
        }
        if (editable) {
            if (this.selectionLayer) {
                this.showSelectionLayer(false);
            }
            this.$changeTemplateMenu && this.$changeTemplateMenu.removeClass("hidden");
        } else {
            if (this.selectionLayer) {
                this.hideSelectionLayer(false);
            }
            this.$changeTemplateMenu && this.$changeTemplateMenu.addClass("hidden");
            this.hideSideBar();
        }
    },

    render: function() {
        this.isShowingUI = true;

        this.$canvasBounds = $("<div id='canvas_bounds'/>");
        this.$canvasContainer = $("<div id='canvas_container'/>");
        this.$el.append(this.$canvasBounds);
        this.$canvasBounds.append(this.$canvasContainer);

        this.$canvasContainer.top(this.CANVAS_TOP);
        this.$canvasContainer.addClass("theme");

        this.menuBar = new EditorMenuBar({ editor: this, ...this.menuBarOptions });
        this.$el.append(this.menuBar.render().$el);
        this.menuBar.$el.width(this.MENU_BAR_WIDTH);
        this.menuBar.$el.left(0);

        // create the selectionLayer
        this.selectionLayer = new SelectionLayer({ editor: this });
        this.$el.append(this.selectionLayer.$el);

        this.$el.fileDrop({
            proxy: true,
            over: e => {
                this.sideBar && app.dialogManager.unregisterDialog(this.sideBar);
                this.hideSideBar(true);
                this.selectionLayer.onDragOver(e);
            },
            drop: e => {
                this.selectionLayer.onDrop(e);
            }
        });

        // create the slideOptions controls (add, dupe, delete slide)
        if (!this.isSlideEditor && !this.hideSlideOptionsControls) {
            this.slideOptions = new SlideOptions();
            this.$el.append(this.slideOptions.render().$el);

            this.slideOptions.$el.on("mousedown", event => {
                this.onClickNothing(event);
            });
        }

        this.$changeTemplateMenu = this.$el.addEl($.div("change_template"));

        if (!this.isSlideDemo && !this.isSlideEditor && !this.isTemplateEditor && !this.hideCollaborationBar) {
            this.collaborationBar = new CollaborationBarContainer();
            this.$collaborationBar = this.$el.addEl(this.collaborationBar.render().$el);
            this.$collaborationBar.top(this.menuHeight).height(`calc(100% - ${this.menuHeight}px`);
        }

        this.$canvasBounds.on("mousedown", event => {
            this.onClickNothing(event);
        });

        this.$canvasContainer.on("mousedown", event => {
            this.onClickNothing(event);
        });

        $("body").on("mousedown", event => {
            this.onClickNothing(event);
        });

        this.$el.addEl(this.collaborators.render().$el);

        this.goPrevSlide = _.throttle(() => {
            this._goPrevSlide();
        }, 350);
        this.goNextSlide = _.throttle(() => {
            this._goNextSlide();
        }, 350);

        if (this.isSlideDemo) {
            let $slideMenu = this.$el.addEl($.div("slide-demo-menu"));
            reactMount(<SlideDemoMenu />, $slideMenu[0]);
        }
        return this;
    },

    checkIfSharedSlideIsReadOnly(slide = ds.selection.slide) {
        return (slide.has("libraryItemId") &&
            (this.$lockedSlideShield || !app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, ds.selection.presentation.getWorkspaceId())));
    },

    _refreshSlide(state = {}, currentTemplate) {
        // Special case updating for a template such as when the slide variation is updated
        if (app.currentCanvas && state.template_id !== currentTemplate) {
            app.currentCanvas.model.template_id = state.template_id;
        }
    },

    renderPreventSlideEdit() {
        if (this.isSlideEditor) return;

        _.defer(() => this._renderPreventSlideEdit(), 500);
    },

    _renderPreventSlideEdit() {
        if (!ds.selection.slide) return;

        const isLibrarySlide = ds.selection.slide && ds.selection.slide.has("libraryItemId");
        const isLocked = isLibrarySlide && !this.isSlideEditor;
        const orgId = ds.selection.presentation.get("orgId");
        const workspaceId = ds.selection.presentation.getWorkspaceId();

        // remove any lock shield
        if (this.$lockedSlideShield) {
            this.$lockedSlideShield.remove();
            this.$lockedSlideShield = null;
        }

        // reset the menubar state
        $(".slide_info").show();
        $(".save-slide-changes, .locked-slide").hide();

        if (isLocked) {
            // lock the slide initially
            app.currentCanvas.lockSlide(null, SlideLockType.ORG);

            // show the lock indicator
            $(".locked-slide").show();

            // create a lock shield to trap clicks on canvas
            this.$lockedSlideShield = $.div("locked-slide-shield");
            this.$el.append(this.$lockedSlideShield);
            this.$lockedSlideShield.setBounds(geom.Rect.FromBoundingClientRect(app.currentCanvas.el.getBoundingClientRect()));

            this.$lockedSlideShield.on("click", () => {
                handleEditSharedSlide();
            });
        }

        if (this.$lockedObsoleteTemplateShield) {
            this.removeLockedObsoleteTemplateShield();
        }

        if (app.currentCanvas.slideTemplate) {
            let requiresTemplateUpdate = app.currentCanvas.slideTemplate.constructor.updateTemplateId != null;

            if (requiresTemplateUpdate) {
                // lock the slide initially
                app.currentCanvas.lockSlide(null, SlideLockType.OBSOLETE_TEMPLATE);

                // create a lock shield to trap clicks on canvas
                this.$lockedObsoleteTemplateShield = $.div("locked-obsolete-template-shield");
                this.$el.append(this.$lockedObsoleteTemplateShield);
                setTimeout(() => {
                    const boundingClientRect = app.currentCanvas.el.getBoundingClientRect();
                    const shieldBounds = geom.Rect.FromBoundingClientRect(boundingClientRect);
                    this.$lockedObsoleteTemplateShield.setBounds(shieldBounds);
                }, 500);

                this.$lockedObsoleteTemplateShield.on("click", () => {
                    ShowDialog(UpdateTemplateVersionDialog, {
                        canvas: app.currentCanvas,
                        onUpdateTemplate: () => {
                            this.removeLockedObsoleteTemplateShield();
                        }
                    });
                });
            }
        }

        if (app.currentCanvas.isEditable) {
            app.currentCanvas.setCollaborationLockIfNeeded();
        }
    },

    removeLockedObsoleteTemplateShield: function() {
        app.currentCanvas.unlockSlide(SlideLockType.OBSOLETE_TEMPLATE);
        this.$lockedObsoleteTemplateShield.remove();
        this.$lockedObsoleteTemplateShield = null;
        this.updateCurrentCanvasUI();
    },

    showEditorMenuBar: function() {
        this.menuBar.delegateEvents();
    },

    hideEditorMenuBar: function() {
        this.menuBar.undelegateEvents();
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Loading
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    loadPresentation: function(startSlideIndex = 0) {
        const shouldOpenComments = new URL(window.location.href).searchParams.get("comments") !== null;

        this.listenTo(ds.selection.presentation, "permissionError", model => {
            ShowWarningDialog({
                title: "Permission Error",
                message: "You no longer have permissions to write to this presentation.",
                acceptCallback: async () => {
                    if (model.invalid) {
                        //to ensure the presentation library shows the correct permission for the presentation refetch the user permissions
                        await model.getUserPermissions(true);
                    }
                    app.router.navigate("/", { trigger: true });
                }
            });
        });

        ds.selection.presentation.prepareSlides(false);
        startSlideIndex = Math.clamp(startSlideIndex, 0, ds.selection.presentation.slides.length - 1);
        this.slides = [];

        this.listenTo(ds.selection.presentation, "slidesUpdated", (presentationSlides, options) => {
            let editorSlides = this.slides;
            let currentSlideRemoved = -1;

            presentationSlides.forEach(slide => {
                let existingEditorSlide = editorSlides.find(editorSlide => editorSlide.model === slide);
                if (!existingEditorSlide) {
                    this._createSlide(slide);
                }
            });
            _.forEachRight(editorSlides, (editorSlide, index) => {
                let existingPresentationSlide = presentationSlides.has(editorSlide.model);
                if (!existingPresentationSlide) {
                    this._removeSlide(editorSlide.model);
                    if (editorSlide.model === ds.selection.slide) {
                        currentSlideRemoved = index;
                    }
                }
            });
            this._sortSlides();
            if (currentSlideRemoved > -1) {
                if (currentSlideRemoved >= this.slides.length) {
                    this.goToSlideByIndex(this.slides.length - 1);
                } else {
                    this.goToSlideByIndex(currentSlideRemoved);
                }
            } else {
                this.layout(false).then(() => {
                    this.updateSlideInfo(this.getCurrentSlideIndex());
                    this.setCurrentCanvas();
                });
            }
        });

        //setup all of the slides that are already loaded
        ds.selection.presentation.slides.each((slideModel, index) => {
            let slide = this._addSlide(slideModel);
            slide.canvas.$el.opacity(0);
        });
        this._sortSlides();

        // render and layout the first slide before resolving the promise
        if (ds.selection.presentation.slides.length) {
            var firstSlide = this.slides[startSlideIndex];
            //set the selection to the first slide
            log.info(log.EDITOR)("Editing template type: " + firstSlide.model.get("template_id"));
            ds.selection.set("slide", firstSlide.model, { silent: true });
            ds.selection.trigger("firstSlideSet");
            return this.setCurrentCanvas().then(() => {
                this.loadError = null;
                this.isShowingSlides = true;
                // Initializing comments
                this.initializeComments(shouldOpenComments);
                this.trigger("editor-ready");
                this.collaborators.renderCollaborators();
                return this.resize(false);
            }).catch(err => {
                if (this.loadError) {
                    return Promise.reject(this.loadError);
                } else {
                    this.loadError = err;
                }
                if (startSlideIndex !== 0) {
                    return this.loadPresentation(0);
                } else if (this.slides.length > 1) {
                    return this.loadPresentation(1);
                } else {
                    return Promise.reject(err);
                }
            });
        } else {
            return Promise.resolve();
        }
    },

    /**
     * @private
     */
    _sortSlides: function() {
        this.slides = ds.selection.presentation.sortSlides(this.slides, slide => {
            return slide.model.id;
        });
        this.canvasRenderer.setCanvases(this.slides.map(slide => {
            return slide.canvas;
        }));
    },

    /**
     * @private
     * @param model
     * @param isNew
     * @returns {{model: *}}
     */
    _addSlide: function(model, isNew) {
        // create an object that will link the slide model and the slide canvas
        var slide = {
            model: model
        };
        // create a new SlideCanvas for the slide model and add it to the DOM- we won't render the canvas until prerender is called
        slide.canvas = new SlideCanvas({
            dataModel: model,
            canvasWidth: this.CANVAS_WIDTH,
            canvasHeight: this.CANVAS_HEIGHT,
            editable: true,
            isNew: isNew,
            disableCollaborationLocks: this.disableCollaborationLocks
        });
        this.$canvasContainer.append(slide.canvas.render().$el);

        this.wireSlideEvents(slide);

        // add it to the slides array and resort
        this.slides.push(slide);

        return slide;
    },

    /**
     * @private
     * @param slideModel
     */
    _createSlide: function(slideModel) {
        let slide = this._addSlide(slideModel, true);
        slide.canvas.renderSlide();
        this.layout(false);
    },

    /**
     * @private
     * @param slideModel
     */
    _removeSlide: function(slideModel) {
        let slide = this.getSlideByModel(slideModel);

        if (slide) {
            slide.canvas.remove();
            this.slides.remove(slide);
        }
    },

    wireSlideEvents: function(slide) {
        slide.canvas.$el.on("mousedown", event => {
            if (!this.isPresenting) {
                if (this.isDragging) return; //don't go to slides while we are dragging in slidegrid

                if (ds.selection.slide != slide.model) {
                    //setting selection element to null so pending team slide change can be saved
                    ds.selection.element = null;
                    this.goToSlide(slide);
                }
            }
        });

        this.listenTo(slide.model, "change:libraryItemId", (model, options) => {
            this.renderPreventSlideEdit();
        });
    },

    getInsertIndex: function() {
        if (ds.selection.slide) {
            return ds.selection.presentation.getSlideIndex(ds.selection.slide.id) + 1;
        } else if (this.slideGrid?.focusedSlide?.getSlideIndex) {
            return this.slideGrid.focusedSlide.getSlideIndex() + 1;
        } else {
            return ds.selection.presentation.slides.length;
        }
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // UI
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    onClickNothing: function(event) {
        if (event.originalEvent && event.originalEvent.hasSelectedElement) {
            return;
        }
        var $target = $(event.target);
        if (!$target.is("body") && !$target.is("#canvas_container") && !$target.is("#canvas_bounds") && $target.closest(".slide_options").length == 0) {
            return;
        }

        // this.setSelectedElement(null);
        ds.selection.element = null;
        ds.selection.rolloverElement = null;
        this.trigger(CanvasEventType.SELECT_NOTHING);
    },

    setClipboardData(data) {
        this.clipboardData = data;
    },

    pasteFromClipboard() {
        if (this.clipboardData) {
            const { dataType, parentElementType } = this.clipboardData;

            if (dataType === "element") {
                if (parentElementType === "AnnotationLayer" && this.currentCanvas.layouter.annotations && this.currentCanvas.layouter.template.allowAnnotations !== false) {
                    this.currentCanvas.layouter.annotations.pasteFromClipboard(this.clipboardData)
                        .then(pastedElement => ds.selection.element = pastedElement);
                } else if (this.currentCanvas.layouter.primary?.type === parentElementType && this.currentCanvas.layouter.primary?.pasteFromClipboard) {
                    this.currentCanvas.layouter.primary.pasteFromClipboard(this.clipboardData)
                        .then(pastedElement => ds.selection.element = pastedElement);
                }
            }
        }
    },

    onKeyDown: function(event) {
        // Ignore key down when the sidebar prevents canvas editing
        if (this.sideBar && this.sideBar.preventCanvasEditingWhenOpen) {
            return;
        }

        // Checking if there are dialogs open (excluding the side bar)
        if (app.dialogManager.openDialogs.some(dialog => dialog !== this.sideBar && !(dialog instanceof EditSharedSlideDialog))) {
            return;
        }

        if (this.slideGrid || event.target.nodeName === "INPUT" || this.isPresenting || app.tour || app.clickShieldEnabled || (event.target.nodeName === "DIV" && event.target.getAttribute("contenteditable") === "true")) {
            return;
        }

        if (this.sideBar && this.sideBar.confirmBeforeClose) {
            return;
        }

        if (this.ignoreArrowKeys && isArrowKey(event.which)) {
            return;
        }

        // Key commands for locked presentations.
        if (this.currentCanvas?.isEditable === false) {
            switch (event.which) {
                case Key.PAGE_UP:
                case Key.LEFT_ARROW:
                case Key.UP_ARROW:
                    this.goPrevSlide();
                    break;
                case Key.PAGE_DOWN:
                case Key.RIGHT_ARROW:
                case Key.DOWN_ARROW:
                    this.goNextSlide();
                    break;
            }
            return;
        }

        if (event.which == 91 || event.which == 17) return;

        if ((event.metaKey || event.ctrlKey) && event.which == Key.KEY_C) {
            if (ds.selection.element && ds.selection.element.canCopyToClipboard) {
                this.setClipboardData(ds.selection.element.getClipboardData());
            } else {
                this.setClipboardData(null);
            }
            return;
        }

        // don't process cmd-r cmd-c cmd-v cmd-x
        if ((event.metaKey || event.ctrlKey) && (event.which == Key.KEY_C || event.which == Key.KEY_V || event.which == Key.KEY_X || event.which == Key.KEY_R)) {
            return;
        }

        if (event.which == Key.TAB) {
            if (app.dialogManager.openDialogs.length) {
                return;
            }
            this.handleTabKey(event);
            return;
        }

        if (event.metaKey || event.ctrlKey) {
            switch (event.which) {
                case Key.KEY_Z:
                    if (event.shiftKey) {
                        app.undoManager.redo();
                    } else {
                        app.undoManager.undo();
                    }
                    this.renderPreventSlideEdit();
                    event.stopPropagation();
                    event.preventDefault();
                    return;
                case Key.KEY_E:
                    if (event.metaKey || event.ctrlKey && event.altKey) {
                        app.themeManager.loadTheme(ds.selection.presentation).then(() => {
                            ds.selection.element = null;
                            this.currentCanvas.getCanvasElement().markStylesAsDirty();
                            this.currentCanvas.refreshCanvas();
                        });
                    }
                    break;
                case Key.KEY_N:
                    ShowDialog(AddSlideContainer);
                    event.preventDefault();
                    return;
                case Key.KEY_D:
                    if (this.currentCanvas.layouter.elements.primary.getSelectionElement().overlay) {
                        this.currentCanvas.layouter.elements.primary.getSelectionElement().overlay.handleKeyboardShortcut(event);
                    }
                    event.preventDefault();
                    return;
                case Key.KEY_1:
                    this.menuBar.showThemePanel();
                    event.preventDefault();
                    break;
                case Key.KEY_2:
                    this.showAddElement();
                    event.preventDefault();
                    break;
                case Key.KEY_4:
                    this.menuBar.showTemplatePanel();
                    event.preventDefault();
                    break;
                case Key.KEY_5:
                    this.menuBar.toggleNotes();
                    event.preventDefault();
                    break;
            }
        }

        if (this.slideGrid) return;
        if (event.target.nodeName != "BODY") return;

        switch (event.which) {
            case Key.PAGE_UP:
            case Key.LEFT_ARROW:
            case Key.UP_ARROW:
                this.goPrevSlide();
                break;
            case Key.PAGE_DOWN:
            case Key.RIGHT_ARROW:
            case Key.DOWN_ARROW:
                this.goNextSlide();
                break;
            case Key.ESCAPE:
                ds.selection.element = null;
                break;
            case Key.DELETE:
            case Key.BACKSPACE:
                if (ds.selection.element && !(ds.selection.element instanceof TextElement) && ds.selection.element.findClosestOfType(CollectionItemElement)) {
                    let collectionItem = ds.selection.element.findClosestOfType(CollectionItemElement);
                    if (!collectionItem.canDelete) {
                        break;
                    }
                    let collectionElement = collectionItem.parentElement;
                    let itemIndex = collectionElement.getItemIndex(collectionItem.id);
                    if (collectionItem.overlay && collectionItem.overlay.onDeleteItem) {
                        collectionItem.overlay.onDeleteItem();
                        return;
                    }
                    collectionElement.deleteItem(collectionItem.id);
                    this.currentCanvas.updateCanvasModel(true).then(() => {
                        if (itemIndex < collectionElement.itemElements.length) {
                            ds.selection.element = collectionElement.itemElements[itemIndex];
                        } else {
                            ds.selection.element = _.last(collectionElement.itemElements);
                        }
                    });
                } else if (ds.selection.rolloverElement && ds.selection.rolloverElement.getElementPath().find(e => e.getInheritancePath().contains("CollectionItemElement")) != null) {
                    let target = ds.selection.rolloverElement.getElementPath().find(e => e.getInheritancePath().contains("CollectionItemElement"));
                    if (!target.canDelete) {
                        break;
                    }
                    target.parentElement.deleteItem(target.id);
                    this.currentCanvas.updateCanvasModel(true);
                }
                event.preventDefault();
                break;
            case Key.SPACE:
                event.preventDefault();
                if (this.slideGrid) {
                    this.hideSlideGrid();
                } else {
                    this.showSlideGrid();
                }
                break;
            case Key.HOME:
                this.goToSlideByIndex(0);
                break;
            case Key.END:
                this.goToSlideByIndex(this.slides.length - 1);
                break;
        }
    },

    onKeyPress(event) {
        if (this.isPresenting) return;
    },

    showContextMenu: function(event) {
        if ($(".modal_dialog:visible").length) {
            event.preventDefault();
            return;
        }

        if ($(event.target).closest(".sidebar, .editor_menu").length) {
            event.preventDefault();
            return;
        }

        if (this.slideGrid) return;

        let menu;

        if (ds.selection.rolloverElement) {
            for (let element of ds.selection.rolloverElement.getElementPath()) {
                menu = element.getContextMenu(event);
                if (menu) {
                    break;
                }
            }
        } else if (ds.selection.element) {
            menu = ds.selection.element.getContextMenu(event);
        }

        if (menu) {
            let $menu = controls._renderMenu(menu.def, () => {
                $menu.clickShield(false);
                $menu.remove();
            }, menu.callback);
            $("body").append($menu);
            $menu.left(Math.min(event.pageX, window.innerWidth - $menu.width())).top(Math.min(event.pageY, window.innerHeight - $menu.height()));

            $menu.clickShield(() => {
                $menu.clickShield(false);
                $menu.remove();
            }, true);
        }
    },

    handlePaste: async function(event) {
        if (
            this.isPresenting ||
            event.target.nodeName === "TEXTAREA" ||
            event.target.nodeName === "INPUT" ||
            (event.target.nodeName === "DIV" && event.target.getAttribute("contenteditable") === "true")
        ) {
            return;
        }

        // Checking if there are dialogs open (excluding the side bar)
        if (app.dialogManager.openDialogs.some(dialog => dialog !== this.sideBar && !(dialog instanceof EditSharedSlideDialog))) {
            return;
        }

        _.each(event.originalEvent.clipboardData.files, item => {
            if (item.type.indexOf("image") == 0 && item.size > ASSET_MAX_SIZE) {
                return ShowWarningDialog({
                    title: "Upload Error",
                    message: `Something went wrong with the upload. Please try dragging and dropping your image instead. Or, reduce the file size (<${ASSET_MAX_SIZE / 1024 / 1024}MB) and try again.`
                });
            }
        });

        if (this.selectionLayer.selectionViews?.[0]?.authoringRef) {
            this.selectionLayer.selectionViews[0].authoringRef.current.handlePaste(event);
        }

        let pasteElement;

        if (!event.originalEvent.clipboardData.files.length) {
            this.pasteFromClipboard();
            return;
        }

        (function checkForPasteHandler(element) {
            if (element && element.canHandlePaste && element.canEdit) {
                pasteElement = element;
            }
            if (element && !_.isEmpty(element.elements)) {
                _.each(element.elements, el => {
                    checkForPasteHandler(el);
                });
            }
        })(ds.selection.element || ds.selection.rolloverElement);

        if (pasteElement) {
            let asset = await handlePasteImage(event);
            pasteElement.model.content_type = "image";
            pasteElement.model.content_value = asset.id;
            this.currentCanvas.updateCanvasModel(false);
        }
    },

    handleTabKey: function(event) {
        if (ds.selection.element || ds.selection.rolloverElement) {
            let elementHandledTabKey = false;

            for (let element of (ds.selection.element || ds.selection.rolloverElement).getElementPath()) {
                if (element.overlay && element.overlay.handleTabKey) {
                    element.overlay.handleTabKey(event);
                    elementHandledTabKey = true;
                }
            }

            if (elementHandledTabKey) {
                event.preventDefault();
                return;
            }
        }

        const isSmartSlide = app.currentCanvas.slideTemplate.canConvertToAuthoring;

        // when using a smart slide, use this logic to cycle through
        // elements within the slide
        if (isSmartSlide) {
            let textBoxes = this.currentCanvas.layouter.canvasElement.findChildElements(TextElement).filter(text => text.canEdit);
            if (textBoxes.length > 0) {
                var index;
                if (ds.selection.element && ds.selection.element instanceof TextElement) {
                    index = textBoxes.indexOf(ds.selection.element);
                    if (event.shiftKey) {
                        index -= 1;
                    } else {
                        index += 1;
                    }
                } else {
                    index = 0;
                }

                index = index % textBoxes.length;
                ds.selection.element = textBoxes[index];
            }
        }
    },

    showUI: function(transition) {
        const slide = ds.selection.slide;
        const isSharedSlide = slide.get("libraryItemId");
        if (this.slideOptions) {
            this.slideOptions.$el.toggleClass("transition", transition);
            this.slideOptions.$el.removeClass("hidden");
        }
        const userCannotEditSharedSlide = !app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, slide.presentation.getWorkspaceId());
        if (isSharedSlide && userCannotEditSharedSlide) {
            return;
        }

        this.showSelectionLayer(transition);

        this.$changeTemplateMenu.toggleClass("transition", transition);

        this.$changeTemplateMenu.removeClass("hidden");
    },

    hideUI: function(transition) {
        if (this.slideOptions) {
            this.slideOptions.$el.toggleClass("transition", transition);
            this.slideOptions.$el.addClass("hidden");
        }

        this.hideSelectionLayer(transition);

        this.$changeTemplateMenu.toggleClass("transition", transition);
        this.$changeTemplateMenu.addClass("hidden");
    },

    showSelectionLayer: function(transition) {
        this.selectionLayer.$el.toggleClass("transition", transition);
        this.selectionLayer.$el.removeClass("hidden");
        this.selectionLayer.isHidden = false;
    },

    hideSelectionLayer: function(transition) {
        this.selectionLayer.$el.toggleClass("transition", transition);
        this.selectionLayer.$el.addClass("hidden");
        this.selectionLayer.isHidden = true;
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Slide Navigation
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    getCurrentSlideIndex: function() {
        let activeSlide;
        if (this.slideGrid) {
            activeSlide = this.slideGrid.focusedSlide.model;
        } else if (ds.selection.slide) {
            activeSlide = ds.selection.slide;
        }
        return activeSlide ? ds.selection.presentation.getSlideIndex(activeSlide.id) : -1;
    },

    getSlideByModel: function(slideModel) {
        return _.find(this.slides, {
            model: slideModel
        });
    },

    getSlideById: function(slideId) {
        return _.find(this.slides, s => s.model.id == slideId);
    },

    getCurrentSlide: function() {
        return this.getSlideByModel(ds.selection.slide);
    },

    _goNextSlide: function() {
        if (this.isPresenting && !this.currentCanvas.isPlaybackComplete()) {
            this.currentCanvas.playNextStage();
        } else if (ds.selection.slide?.loaded) {
            if (ds.selection.slide.hasNextState()) {
                ds.selection.slide.nextState();
            } else {
                const slideIndex = this.getCurrentSlideIndex() + 1;
                if (slideIndex < this.slides.length) {
                    this.goToSlideByIndex(slideIndex);
                }
            }
        } else if (this.getCurrentSlideIndex() >= this.slides.length) {
            // Reset to the first slide if we've gone out of bounds
            this.goToSlideByIndex(0);
        }
        // If the slide isn't loaded and we're not out of bounds, do nothing.
    },

    _goPrevSlide: function() {
        var slideIndex = this.getCurrentSlideIndex() - 1;
        if (slideIndex >= 0) {
            this.goToSlideByIndex(slideIndex);
        }
    },

    goToSlideByIndex: function(index, options) {
        if (this.slides[index]) {
            this.goToSlide(this.slides[index], options);
        }
    },

    goToSlide: function(slide, options) {
        if (ds.selection.slide != slide.model) {
            if (ds.selection.slide && ds.selection.slide.dataStateIndex != 0) {
                ds.selection.slide.setDataState(0);
            }
            log.info(log.EDITOR)("Editing template type: " + slide.model.get("template_id"));
            ds.selection.set("slide", slide.model, options);

            const target = this.getSlideByModel(slide.model);
            target?.canvas?.beforeShowElements();
        }
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Slide Management
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    hideSlides: function() {
        this.isShowingSlides = false;
        this.$canvasContainer.find(".slide_canvas").not(".current_slide").opacity(0);
    },

    duplicateSlide: function(slideModel) {
        const workspaceId = ds.selection.presentation.getWorkspaceId();
        const insertIndex = this.getInsertIndex();
        ds.selection.presentation.duplicateSlide(slideModel, { insertIndex }).then(newSlide => {
            const duplicateSlideCount = 1;
            const eventProps = {
                "slide_ids": [newSlide[0].get("id")],
                "source_slides": [slideModel.get("id")],
                "slide_template_names": [slideTemplates[slideModel.get("template_id")].title],
                "slides_created": duplicateSlideCount
            };
            trackActivity("Slide", "Duplicate", null, duplicateSlideCount, eventProps, { audit: true });

            return this.goNextSlide();
        }).catch(err => ErrorHandler.handleSlideLimitReached(err, { workspaceId }));
    },

    deleteSlide: function(slideModel) {
        if (this.slides.length > 1) {
            ds.selection.presentation.destroySlides(slideModel.id);
        } else {
            ShowWarningDialog({
                title: "Can't delete slide",
                message: "Sorry, you can't delete the only slide in a presentation"
            });
        }
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Canvas Layout
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    getContainerBounds: function() {
        let containerBounds;
        let appHeight = this.$el.height();

        const hasLeftOrRightSideBar = this.isShowingSideBar && this.sideBar && (this.sideBarDirection == DirectionType.LEFT || this.sideBarDirection == DirectionType.RIGHT);
        const sideBarWidth = this.sideBar?.$el?.outerWidth();

        if (this.isPresenting) {
            containerBounds = new geom.Size(window.innerWidth, appHeight);
        } else {
            let x = 0;
            let y = this.MENU_HEIGHT;
            let availableWidth = Math.max(this.$el.width(), this.MINIMUM_CONTAINER_WIDTH) - this.MENU_BAR_WIDTH - this.USER_MENU_BAR_WIDTH;
            let availableHeight = Math.max(appHeight, this.MINIMUM_CONTAINER_HEIGHT) - this.MENU_HEIGHT - this.CANVAS_TOP - this.CANVAS_BOTTOM; //- 125;
            // availableHeight = Math.min(availableHeight, 900);

            // Shrinking the editor to fit the comments panel
            if (this.isCommentsOpen) {
                availableWidth -= this.COMMENTS_BAR_WIDTH;
            }

            if (hasLeftOrRightSideBar) {
                availableWidth -= sideBarWidth;
                if (this.sideBarDirection == DirectionType.LEFT) {
                    x += sideBarWidth;
                }
            }

            if (this.isShowingSideBar && this.sideBarDirection == DirectionType.BOTTOM) {
                availableHeight -= this.sideBar.$el.height();
                availableHeight += this.CANVAS_BOTTOM + (this.sideBar.verticalGapOffset || 0);
            }

            if (availableHeight > 900) {
                availableHeight = 900;
            }
            containerBounds = new geom.Rect(x, y, availableWidth, availableHeight);
        }

        // calculate available canvas size
        let availableCanvasWidth = containerBounds.width;
        let availableCanvasHeight = containerBounds.height;

        if (!this.isPresenting) {
            let canvasPadding = this.CANVAS_PADDING;

            // Accounting for side pannels and hiding side slides if there's not enough space
            let containerBoundsLimit = 1000;
            if (this.isCommentsOpen) {
                containerBoundsLimit += 280;
            }
            if (hasLeftOrRightSideBar) {
                containerBoundsLimit += sideBarWidth;

                if (this.sideBar.preventCanvasEditingWhenOpen) {
                    canvasPadding = 20;
                }
            }

            if (containerBounds.width < containerBoundsLimit) {
                canvasPadding = Math.min(canvasPadding, 90);
                this.hideSideSlides = true;
            } else {
                this.hideSideSlides = false;
            }

            // account for horizontal canvas padding
            availableCanvasWidth -= canvasPadding * 2;
        }

        // calculate canvas scale from available canvas size size relative to the absolute canvas size
        let canvasScale = Math.min(availableCanvasHeight / this.CANVAS_HEIGHT, availableCanvasWidth / this.CANVAS_WIDTH);
        canvasScale = parseFloat(canvasScale.toFixed(3));

        // debug() forceCanvas
        if (this.forceCanvasScale) {
            canvasScale = this.forceCanvasScale;
        }

        // calculate scaled canvas size
        let canvasWidth = this.CANVAS_WIDTH * canvasScale;
        let canvasHeight = this.CANVAS_HEIGHT * canvasScale;

        let canvasX = containerBounds.width / 2 - canvasWidth / 2;
        let canvasY = this.$canvasBounds.position().top + this.$canvasContainer.position().top + parseInt(this.$canvasContainer.css("padding-top")) + canvasHeight / 2 - this.CANVAS_HEIGHT * canvasScale / 2;

        const canvasBounds = new geom.Rect(canvasX, canvasY, canvasWidth, canvasHeight);
        canvasBounds.scale = canvasScale;

        return {
            containerBounds: containerBounds,
            canvasBounds: canvasBounds
        };
    },

    calcEditorLayout: function() {
        let slideIndex = this.getCurrentSlideIndex();

        let bounds = this.getContainerBounds();
        let maxCanvasScale = bounds.canvasBounds.scale;
        let minCanvasScale = this.MINIMUM_SLIDE_SCALE * bounds.canvasBounds.scale;

        let slideSpace = this.CANVAS_WIDTH * minCanvasScale + this.SLIDE_GAP;

        let x = bounds.containerBounds.x + bounds.containerBounds.width / 2 - bounds.canvasBounds.width / 2;
        x -= slideIndex * slideSpace;

        for (let i = 0; i < this.slides.length; i++) {
            let slide = this.slides[i];

            slide.calculatedLayout = {
                translateX: x
            };

            if (i == slideIndex) {
                slide.calculatedLayout.scale = maxCanvasScale;
                slide.calculatedLayout.opacity = 1;
            } else {
                slide.calculatedLayout.scale = minCanvasScale;
                slide.calculatedLayout.opacity = this.hideSideSlides ? 0 : this.MINIMUM_SLIDE_OPACITY;
            }

            slide.calculatedLayout.translateY = parseFloat((bounds.canvasBounds.height / 2 - this.CANVAS_HEIGHT * slide.calculatedLayout.scale / 2).toFixed(3));

            slide.canvas.canvasScale = slide.calculatedLayout.scale;
            slide.canvas.maxCanvasScale = maxCanvasScale;
            x += this.CANVAS_WIDTH * slide.calculatedLayout.scale + this.SLIDE_GAP;
        }
    },

    layout: function(animate, hideUI = true) {
        return new Promise(resolve => {
            if (animate && hideUI) {
                this.hideUI(true);
            }

            this.calcEditorLayout();

            let slideIndex = this.getCurrentSlideIndex();

            for (let i = 0; i < this.slides.length; i++) {
                let slide = this.slides[i];

                if (animate && i >= slideIndex - 2 && i < slideIndex + 2) {
                    slide.canvas.$el.css("transition", "500ms");
                    if (i == slideIndex) {
                        slide.canvas.$el.one("transitionend", () => {
                            resolve();
                        });
                    }
                } else {
                    slide.canvas.$el.css("transition", "none");
                }

                let currentTransform = slide.canvas.$el.getTransform();

                if (Math.round(slide.calculatedLayout.translateX) != currentTransform.x || Math.round(slide.calculatedLayout.translateY) != currentTransform.y || slide.calculatedLayout.scale != currentTransform.scale) {
                    slide.canvas.$el.transform({
                        translateX: Math.round(slide.calculatedLayout.translateX),
                        translateY: Math.round(slide.calculatedLayout.translateY),
                        scale: slide.calculatedLayout.scale
                    }).opacity(slide.calculatedLayout.opacity);
                } else {
                    resolve();
                }
            }

            if (!animate) {
                resolve();
            }
        });
    },

    resize: async function(animate, hideUI = true) {
        if (this.isPresenting) return;

        //update the canvasContainer since it is used to compute the container bounds.
        this.$canvasContainer.left(this.MENU_BAR_WIDTH).top(this.CANVAS_TOP);

        let bounds = this.getContainerBounds();
        app.canvasScale = bounds.canvasBounds.scale;

        // position and size editor DOM elements
        this.$canvasContainer.outerWidth(bounds.containerBounds.width).outerHeight(this.$el.height() - this.$canvasContainer.offset().top);
        this.selectionLayer.$el.left(this.MENU_BAR_WIDTH + bounds.containerBounds.x + bounds.canvasBounds.x).top(bounds.canvasBounds.y).width(bounds.canvasBounds.width).height(bounds.canvasBounds.height);
        this.selectionLayer.scale = bounds.canvasBounds.scale;

        if (this.slideOptions) {
            this.slideOptions.$el.setBounds(new geom.Rect(this.MENU_BAR_WIDTH + bounds.containerBounds.x + bounds.canvasBounds.x + bounds.canvasBounds.width, bounds.canvasBounds.y, this.SLIDE_GAP, bounds.canvasBounds.height));
        }

        //added more to the height so that the variations option is covered.
        const lockedSlideShieldBounds = new geom.Rect(this.selectionLayer.$el.left(), this.selectionLayer.$el.top(), this.selectionLayer.$el.width(), this.selectionLayer.$el.height() + 100);

        if (this.$lockedSlideShield) {
            this.$lockedSlideShield.setBounds(lockedSlideShieldBounds);
        }

        this.$changeTemplateMenu.left(bounds.containerBounds.left + bounds.canvasBounds.right - 45).top(bounds.canvasBounds.bottom + 26);

        // clear selection on resize rather than dealing with re-laying out ui controls
        ds.selection.element = null;
        ds.selection.rolloverElement = null;

        // re-layout canvases
        await this.layout(animate, hideUI);

        this.selectionLayer.refreshSelectionLayer();
        //keeping this after layout is complete as well so that it moves to cover canvas when canvas resizes.
        if (this.$lockedSlideShield) {
            this.$lockedSlideShield.setBounds(lockedSlideShieldBounds);
        }
    },

    setCurrentCanvas: async function(forceFastLoad = false) {
        if (this.slideGrid) return; // don't set current canvas while slidegrid is open

        let bounds = this.getContainerBounds();
        app.canvasScale = bounds.canvasBounds.scale;
        let slide = this.getCurrentSlide();

        if (!slide) {
            return; //This can happen with RTC where the slide can be deleted.
        }

        if (this.currentCanvas === slide.canvas) {
            this.showUI();
            this.currentCanvas.setAsCurrentCanvas(ds.selection.slide);
            this.currentCanvas.setCollaborationLockIfNeeded();
            return;
        }

        ds.selection.element = null;

        if (this.currentCanvas) {
            // REFACTOR TODO
            // if (!this.currentCanvas.model.destroyed) {
            //     this.currentCanvas.model.commit();
            // }
            this.currentCanvas.removeAsCurrentCanvas();
            this.stopListening(this.currentCanvas, "update_spellcheck");
        }

        this.currentSlide = slide;
        this.currentCanvas = slide.canvas;
        await this.currentCanvas.resetPlayback(false);
        // We don't want to go further if there was a subsequent setCurrentCanvas() call
        // while we were waiting for the resetPlayback() call
        if (slide.canvas !== this.currentCanvas) {
            return;
        }

        this.currentCanvas.canvasScale = bounds.canvasBounds.scale;

        this.currentCanvas.setAsCurrentCanvas(ds.selection.slide);
        const currentIndex = this.getCurrentSlideIndex();

        // asset chooser should reset it's default tab when we switch slides
        app.dialogManager.assetChooser && app.dialogManager.assetChooser.resetDefaultTab();

        app.currentCanvas = this.currentCanvas;

        if (!this.isSlideEditor && !this.disableRouterActions) {
            const presentationId = ds.selection.presentation.id;
            const slideNumber = ds.selection.presentation.getSlideIndex(slide.model.id) + 1;
            app.router.navigate(`/${presentationId}/${slideNumber}`);
            app.appState.showEditor(presentationId, slideNumber);
        }

        // Reset editor lock state
        this.lockModel.resetLockState();

        this.trigger("setCurrentCanvas");
        this.updateSlideInfo(currentIndex);

        const ready = await this.canvasRenderer.renderSlide(currentIndex, forceFastLoad);
        this.renderPreventSlideEdit();
        if (ready && currentIndex === this.getCurrentSlideIndex()) {
            this.updateCurrentCanvasUI();
        }

        this.menuBar.renderSlideInfo(ds.selection.slide);
    },

    updateSlideInfo: function(slideIndex) {
        if (typeof slideIndex !== "number") {
            slideIndex = 0;
        } else {
            //slideIndex can be -1 if in a view that does not have a selected slide, ie: SlideGrid.
            slideIndex = Math.max(slideIndex, 0);
        }

        $(".slide_info label").text(`Slide ${slideIndex + 1} of ${this.slides.length}`);
    },

    updateCurrentCanvasUI: function() {
        if (this.slideGrid) return;

        this.currentCanvas.prepareToShowElements();

        // show the selectionLayer and slideOptions - this won't actually make them visible but set display: block while opacity
        // is still zero from the hideUI call but this allows us to update the widgets and get correct layout info before calling showUI
        this.selectionLayer.$el.show();

        if (this.slideOptions) {
            this.slideOptions.$el.show();
        }

        this.selectionLayer.updateCurrentCanvas(this.currentCanvas);

        this.$changeTemplateMenu.empty();

        try {
            let template = this.currentCanvas.layouter.template;

            if (template.variations) {
                this.$changeTemplateMenu.append(controls.createButton(this, {
                    label: "Variations",
                    icon: "lightbulb_outline",
                    callback: () => {
                        this.showSideBar(new VariationsPanel({
                            styles: Variations[template.variations],
                            canvas: this.currentCanvas
                        }), true);
                    }
                }));
            }

            $("#elements").toggleClass("disabled", !template.allowAnnotations);
            $("#layout").toggleClass("disabled", !template.allowLayouts);
            $("#theme").toggleClass("disabled", !template.allowTheming);
            $("#animation_panel").toggleClass("disabled", !template.allowAnimations);
            $("#audio_panel").toggleClass("disabled", !template.allowAnimations);

            // TODO!
            // if (template.constructor.id == "slidePlaceholder") {
            //     this.$addContentMenu.hide();
            // } else {
            //     this.$addContentMenu.show();
            // }

            // this.$addAnnotationButton.toggle(template.allowAnnotations);
        } catch (e) {
            log.error(e);
        }
        //for library slides we only want to show slide options again for users/members who cannot edit the library slide ELSE we can show the UI for those who can edit the slide
        if (ds.selection.slide.has("libraryItemId") && !app.user.features.isFeatureEnabled(FeatureType.EDIT_LIBRARY_ITEMS, ds.selection.presentation.getWorkspaceId())) {
            if (this.slideOptions) {
                this.slideOptions.$el.removeClass("hidden");
            }
        } else {
            this.showUI(true);

            // this is ugly but we need this to hide the variations button on initial load  while on a team slide BA-10415
            if (ds.selection.slide.has("libraryItemId")) {
                this.$changeTemplateMenu.addClass("hidden");
            }
        }
        if (!this.isSlideEditor) {
            this.setEditorLockState(app.currentCanvas.isEditable, ds.selection.slide.has("libraryItemId"));
        }

        $(".slide_info label").text(`Slide ${this.getCurrentSlideIndex() + 1} of ${this.slides.length}`);

        if (this.$slideTemplateButton) {
            this.$slideTemplateButton.remove();
        }
        this.$slideTemplateButton = controls.createPopupButton(this, {
            label: "Template",
            className: "slide-template",
            ...getSlideMenu(),
            overwriteTriggerHandler: false,
        });
        $(".slide_info").after(this.$slideTemplateButton);

        this.$slideTemplateButton.find("label").text(this.getCurrentSlideTemplateName());
        if (this.currentCanvas.slideTemplate.constructor.id == "authoring") {
            this.$slideTemplateButton.css("background", themeColors.authoringThemeBaseColor);
        } else {
            this.$slideTemplateButton.css("background", themeColors.smartSlideThemeBaseColor);
        }

        if (this.currentCanvas.dataModel.get("isSkipped")) {
            $(".unskip-slide-button").show();
        } else {
            $(".unskip-slide-button").hide();
        }

        if (this.slideOptions) {
            this.slideOptions.update();
        }
    },

    getCurrentSlideTemplateName() {
        return this.currentCanvas.slideTemplate.constructor.title;
    },

    playAnimation: function() {
        if (this.currentCanvas) {
            this.hideSelectionLayer();
            this.currentCanvas.animate()
                .then(() => this.showSelectionLayer());
        }
    },

    showSlideGrid: function() {
        ds.selection.element = null;
        this.hideUI(false);
        this.collaborationBar?.render();
        app.mainView.currentMenu.hideUnskipButton();
        app.mainView.currentMenu.hideSlideLock();

        if (this.sideBar) {
            this.hideSideBar(true);
        }

        if (this.$lockedSlideShield) {
            this.$lockedSlideShield.hide();
        }

        if (this.$lockedObsoleteTemplateShield) {
            this.$lockedObsoleteTemplateShield.hide();
        }

        this.collaborationBar?.onShowSlideGrid();

        $(".slide_menu").hide();

        if (this.$slideTemplateButton) {
            this.$slideTemplateButton.hide();
        }

        // unset the currentCanvas to let the slide model know user finished editing the slide
        if (this.currentCanvas) {
            this.currentCanvas.removeAsCurrentCanvas();
            this.currentCanvas = null;
        }

        // create a slidegrid
        this.slideGrid = new SlideGrid({ editorView: this });
        this.slideGrid.render();
        this.slideGrid.$el.opacity(0);
        this.$el.prepend(this.slideGrid.$el);

        this.listenTo(this.slideGrid, "close", () => {
            this.hideSlideGrid();
        });

        this.slideGrid.scrollToFocusedSlide();

        let slideLoc = this.slideGrid.getFocusedSlidePosition();

        let currentSlide = this.getSlideByModel(ds.selection.slide);
        for (let slide of this.slides) {
            if (slide !== currentSlide) {
                slide.canvas.$el.opacity(0);
            }
        }

        if (this.isCommentsOpen) {
            this.closeComments();
        }

        currentSlide.canvas.$el.css("transition", "transform 300ms");
        currentSlide.canvas.$el.transform({
            translateX: slideLoc.x + 40,
            translateY: slideLoc.y + 15,
            scale: this.slideGrid.colWidth / this.CANVAS_WIDTH
        });

        this.slideGrid.$el.velocity("transition.fadeIn", {
            delay: 150,
            complete: () => {
                this.slideGrid.onBecameVisible();
            }
        });
        $("#canvas_bounds").velocity("transition.fadeOut", {
            delay: 150
        });

        $(".slide_info label").text(`${this.slides.length} slides`);
        $(".slide_template_name, .slide_info + .vertical_rule").hide();
        ds.selection.set("slide", null);
    },

    hideSlideGrid: function() {
        if (this.slideGrid) {
            $(".slide_menu").show();

            if (this.$lockedSlideShield) {
                this.$lockedSlideShield.show();
            }

            if (this.$lockedObsoleteTemplateShield) {
                this.$lockedObsoleteTemplateShield.show();
            }

            this.collaborationBar?.onHideSlideGrid();

            ds.selection.set("slide", this.slideGrid.focusedSlide.model, { skipScroll: true });

            let currentSlide = this.getSlideByModel(ds.selection.slide);
            for (let slide of this.slides) {
                if (slide !== currentSlide) {
                    slide.canvas.$el.opacity(this.hideSideSlides ? 0 : this.MINIMUM_SLIDE_OPACITY);
                }
            }

            let slideLoc = this.slideGrid.getFocusedSlidePosition();

            $("#canvas_bounds").show();
            currentSlide.canvas.$el.transform({
                translateX: slideLoc.x + 40,
                translateY: slideLoc.y + 15,
                scale: this.slideGrid.colWidth / this.CANVAS_WIDTH
            });

            $("#canvas_bounds").velocity("transition.fadeIn");

            let slideGrid = this.slideGrid;

            slideGrid.$el.velocity("transition.fadeOut", {
                complete: () => {
                    slideGrid.remove();
                    this.slideGrid = null;
                    $("#canvas_container").show();
                    this.setCurrentCanvas();
                    if (this.$slideTemplateButton) {
                        this.$slideTemplateButton.show();
                    }
                    this.collaborators.renderCollaborators();
                    this.showUI(false);
                    this.collaborationBar?.render();
                }
            });

            this.resize(true);
        }
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // PresentationSettings
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    showPresentationSettings: function(startPane = "share") {
        this.hideSideBar(false, false);
        this.hideUI(false);

        setGlobal({
            presentation: ds.selection.presentation,
            presentationId: ds.selection.presentation.id
        });
        ShowDialog(PresentationSettingsContainer, {
            startPane,
            onClose: () => {
                if (this.slideGrid) {
                    this.isPresentationSettingsOpen = false;
                } else {
                    this.hidePresentationSettings();
                }
            }
        });

        this.collaborators.renderCollaborators();

        this.isPresentationSettingsOpen = true;
    },

    hidePresentationSettings: function() {
        this.showUI();
        this.collaborators.renderCollaborators();
        this.isPresentationSettingsOpen = false;
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Speaker Notes
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    toggleSpeakerNotes: function() {
        if (this.isShowingSpeakerNotes) {
            this.hideSpeakerNotes();
        } else {
            this.showSpeakerNotes();
        }
    },

    showSpeakerNotes: function() {
        if (!this.isShowingSpeakerNotes) {
            this.showSideBar(new SpeakerNotes({ editor: this }));
            this.isShowingSpeakerNotes = true;
            trackActivity("Notes", "Show");
        } else {
            this.sideBar.$el.find("textarea").val(ds.selection.slide.get("slide_notes"));
        }
    },

    hideSpeakerNotes: function() {
        if (this.isShowingSpeakerNotes) {
            this.hideSideBar(true);
            this.isShowingSpeakerNotes = false;
            trackActivity("Notes", "Hide");
        }
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Animation Dialog
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    isShowingAnimationDialog: function() {
        return this.isShowingSideBar && this.sideBar instanceof AnimationDialog;
    },

    toggleAnimationDialog: function() {
        if (this.isShowingAnimationDialog()) {
            this.hideAnimationDialog();
        } else {
            trackActivity("Editor", "ShowAnimationPanel");
            this.showAnimationDialog();
        }
    },

    showAnimationDialog: function() {
        if (!this.isShowingAnimationDialog()) {
            this.showSideBar(new AnimationDialog({
                editor: this,
                isReadOnly: this.checkIfSharedSlideIsReadOnly()
            }));
            trackActivity("Animation", "Show");
        }
    },

    hideAnimationDialog: function() {
        if (this.isShowingAnimationDialog()) {
            this.hideSideBar(true);
            trackActivity("Animation", "Hide");
        }
    },

    isShowingAnimationPanel: function() {
        return this.isShowingSideBar && this.sideBar instanceof AnimationDialog && !this.sideBar.showAudio;
    },

    isShowingAudioPanel: function() {
        return this.isShowingSideBar && this.sideBar instanceof AnimationDialog && this.sideBar.showAudio;
    },

    /// NEW ///

    toggleAnimationPanel: function() {
        if (this.isShowingAnimationPanel()) {
            this.hideAnimationPanel();
        } else {
            trackActivity("Editor", "ShowAnimationPanel");
            this.showAnimationPanel();
        }
    },

    showAnimationPanel: function() {
        if (!this.isShowingAnimationPanel()) {
            this.showSideBar(
                new EditorAnimationPanel({
                    showAudio: false,
                    editor: this,
                    isReadOnly: this.checkIfSharedSlideIsReadOnly()
                }),
                false,
                DirectionType.LEFT
            );
            trackActivity("Animation", "Show");
        }
    },

    hideAnimationPanel: function() {
        if (this.isShowingAnimationPanel()) {
            this.hideSideBar(true);
            trackActivity("Animation", "Hide");
        }
    },

    toggleAudioPanel: function() {
        if (this.isShowingAudioPanel()) {
            this.hideAudioPanel();
        } else {
            trackActivity("Editor", "ShowAudioPanel");
            this.showAudioPanel();
        }
    },

    showAudioPanel: function() {
        if (!this.isShowingAudioPanel()) {
            this.showSideBar(
                new EditorAnimationPanel({
                    showAudio: true,
                    editor: this,
                    isReadOnly: this.checkIfSharedSlideIsReadOnly()
                }),
                true,
                DirectionType.LEFT
            );
            trackActivity("Audio", "Show");
        }
    },

    hideAudioPanel: function() {
        if (this.isShowingAudioPanel()) {
            this.hideSideBar(true);
            trackActivity("Audio", "Hide");
        }
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Sidebar
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    showSideBar: function(view, hasClickShield = false, fromDirection = DirectionType.BOTTOM) {
        let appHeight = this.$el.height();

        if (this.isShowingSideBar) {
            let currentSideBar = this.sideBar;

            this.hideSideBar(true);
            if (this.sideBar && this.sideBar.confirmBeforeClose) {
                return;
            }
            // if we are trying to show the same type of sidebar that is already open, just toggle it closed
            if (currentSideBar && currentSideBar.type == view.type) {
                return;
            }
        }

        if (this.currentCanvas) {
            this.currentCanvas.stopPlayback();
        }

        this.sideBar = view;

        this.hideUI(true);

        if (this.sideBar.isPersistent && this.sideBar.isRendered) {
            this.$el.append(this.sideBar.$el);
        } else {
            this.$el.append(this.sideBar.render().$el);
        }
        this.sideBar.open();

        this.sideBar.$el.show().opacity(0);

        switch (fromDirection) {
            case DirectionType.BOTTOM: {
                view.$el.left(this.MENU_BAR_WIDTH);
                view.$el.outerWidth(window.innerWidth - this.MENU_BAR_WIDTH - this.USER_MENU_BAR_WIDTH);

                let height = Math.max(appHeight - this.getContainerBounds().canvasBounds.bottom - 70, this.sideBar.getPanelHeight());

                view.$el.height(height);
                this.sideBar.$el.velocity({
                    translateY: [appHeight - this.sideBar.$el.height(), appHeight],
                    opacity: [1, 1]
                }, {
                    duration: this.UI_ANIMATION_DURATION,
                    complete: () => {
                        this.sideBar.onAnimationComplete();
                        this.trigger("sidebar_shown");
                    }
                });
                this.collaborators.$el.velocity({
                    translateY: -height
                }, {
                    duration: this.UI_ANIMATION_DURATION
                });

                break;
            }
            case DirectionType.LEFT:
                this.sideBar.$el.top(this.menuHeight);
                this.sideBar.$el.height(this.$el.height() - this.menuHeight);

                this.sideBar.$el.velocity({
                    translateX: [this.MENU_BAR_WIDTH, this.MENU_BAR_WIDTH - this.sideBar.$el.outerWidth() - 40],
                    opacity: [1, 1]
                }, {
                    duration: this.UI_ANIMATION_DURATION,
                    complete: event => {
                        this.sideBar.onAnimationComplete();
                        this.trigger("sidebar_shown");
                    }
                });

                break;

            case DirectionType.RIGHT:
                this.sideBar.$el.top(this.menuHeight);
                this.sideBar.$el.height(this.$el.height() - this.menuHeight);

                this.sideBar.$el.velocity({
                    translateX: [window.innerWidth - this.sideBar.$el.outerWidth(true) - 60, window.innerWidth - 60],
                    opacity: [1, 1]
                }, {
                    duration: this.UI_ANIMATION_DURATION,
                    complete: event => {
                        this.sideBar.onAnimationComplete();
                        this.trigger("sidebar_shown");
                    }
                });
                break;
        }

        if (hasClickShield) {
            view.$el.clickShield(() => this.hideSideBar(true), true, 99);
        }

        this.isShowingSideBar = true;
        this.sideBarDirection = fromDirection;

        this.resize(true)
            .then(() => {
                if (!view.preventCanvasEditingWhenOpen) {
                    this.showUI();
                }
            });
    },

    hideSideBar: function(animate, resizeWhenComplete = true) {
        if (this.sideBar && this.sideBar.confirmBeforeClose) {
            this.sideBar.showConfirmationDialog()
                .then(() => {
                    this.completeHideSideBar(animate, resizeWhenComplete);
                });
        } else {
            this.completeHideSideBar(animate, resizeWhenComplete);
        }
    },

    completeHideSideBar: function(animate, resizeWhenComplete) {
        let appHeight = this.$el.height();

        if (this.sideBar) {
            let closingSideBar = this.sideBar;
            closingSideBar.beforeClose();
            closingSideBar.$el.clickShield(false);

            let removeSideBar = () => {
                app.dialogManager.unregisterDialog(closingSideBar);

                _.delay(() => {
                    if (closingSideBar.isPersistent) {
                        closingSideBar.$el.detach();
                    } else {
                        closingSideBar.remove();
                    }
                    closingSideBar = null;
                }, 200);
                this.trigger("sidebar_hidden");
            };

            if (animate) {
                this.hideUI(true);
                switch (this.sideBarDirection) {
                    case DirectionType.BOTTOM:
                        closingSideBar.$el.velocity({
                            translateY: appHeight
                        }, {
                            duration: this.UI_ANIMATION_DURATION,
                            complete: () => removeSideBar()
                        });
                        this.collaborators.$el.velocity({
                            translateY: 0
                        }, {
                            duration: this.UI_ANIMATION_DURATION
                        });

                        break;
                    case DirectionType.LEFT:
                        closingSideBar.$el.velocity({
                            translateX: this.MENU_BAR_WIDTH - closingSideBar.$el.outerWidth() - 40
                        }, {
                            duration: this.UI_ANIMATION_DURATION,
                            complete: () => removeSideBar()
                        });
                        break;
                    case DirectionType.RIGHT:
                        closingSideBar.$el.velocity({
                            translateX: window.innerWidth
                        }, {
                            duration: this.UI_ANIMATION_DURATION,
                            complete: () => removeSideBar()
                        });
                        break;
                    default:
                        closingSideBar.$el.velocity("transition.fadeOut", {
                            duration: this.UI_ANIMATION_DURATION,
                            complete: () => removeSideBar()
                        });
                        break;
                }
            } else {
                this.collaborators.$el.velocity({
                    translateY: 0
                }, {
                    duration: this.UI_ANIMATION_DURATION
                });
                removeSideBar();
            }

            this.isShowingSideBar = false;

            if (this.sideBar == closingSideBar) {
                this.sideBar = null;
            }

            if (resizeWhenComplete) {
                this.resize(true).then(() => {
                    if (!this.slideGrid && this.sideBar == null) {
                        this.showUI(true);
                    }
                });
            } else {
                if (!this.slideGrid && this.sideBar == null) {
                    this.showUI(true);
                }
            }
            this.menuBar.$el.find(".selected").removeClass("selected");
        } else {
            this.isShowingSideBar = false;
        }
    },

    resizeSideBar: function() {
        switch (this.sideBarDirection) {
            case DirectionType.BOTTOM: {
                this.sideBar.$el.left(this.MENU_BAR_WIDTH);
                this.sideBar.$el.outerWidth(window.innerWidth - this.MENU_BAR_WIDTH);
                let height = Math.max(window.innerHeight - this.getContainerBounds().canvasBounds.bottom - 70, this.sideBar.getPanelHeight());
                if (app.mainView.$verifyEmailBanner) {
                    height -= app.mainView.$verifyEmailBanner.height();
                }
                this.sideBar.$el.height(height);
                this.sideBar.$el.transform({ y: window.innerHeight - height });
                break;
            }
            case DirectionType.LEFT:
                // handled by css
                break;
        }
    },

    showAddElement() {
        const addElement = new AddContentPanel({ element: app.currentCanvas.layouter.elements.primary });
        return this.showSideBar(addElement, true, DirectionType.LEFT);
    },

    showAddAnnotation(container) {
        const addAnnotation = new AddAnnotationPanel({ container });
        return this.showSideBar(addAnnotation, true, DirectionType.LEFT);
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    // Comments
    // ------------------------------------------------------------------------------------------------------------------------------------------------------------
    initializeComments: function(shouldOpenComments) {
        // Initializing the comments container
        this.$commentsContainer = $.div("sidebar comments_container hidden");
        this.$el.append(this.$commentsContainer);

        // Repositioning the comments panel upon the editor resize
        $(window).on("resize.editor", () => {
            this.repositionComments(false);
        });

        if (shouldOpenComments) {
            this.openComments();
        }
    },

    /**
     * Mounts and renders Comments component inside #comments_container div,
     * only calls render() with a new props if the component is already mounted
     */
    renderComments: function() {
        ds.getObservables().setCommentsAreByViewer(false);
        const props = {
            goToSlide: slideIndex => this.goToSlideByIndex(slideIndex),
            close: () => this.closeComments(),
        };
        this.unmountComments = renderReactRoot(EditorCommentsPane, props, this.$commentsContainer[0]);
    },

    /**
     * Toggles the comments panel, invoked by EditorMenu
     */
    toggleComments: function() {
        if (this.isCommentsOpen) {
            this.closeComments();
        } else {
            this.openComments();
        }

        // Setting transition parameter for slide options ("+" button) during resizing the editor to make it moove smoother
        // We can't set it permanently because it'll make window resizing animation ugly
        // this.slideOptions.$el.css("transition", "500ms");
        // this.selectionLayer.$el.css("transition", "500ms");
        this.hideUI(false);

        this.resize(true, false)
            .then(() => {
                // Disabling transition only after the editor resize animation is finished
                // this.slideOptions.$el.css("transition", "none");
                // this.selectionLayer.$el.css("transition", "none");
                this.showUI(true);
            });
    },

    /**
     * Opens the comments panel and forces render() immediately
     */
    openComments: async function() {
        // Removing hidden class because it's assigned upon the editorMenu render
        // to hide the comments container until it's opened for the first time
        this.$commentsContainer.removeClass("hidden");

        $("#toggle_comments").addClass("expanded");

        this.renderComments();
        this.isCommentsOpen = true;
        await this.repositionComments(true);
    },

    /**
     * Closes the comments panel end forces render() upon the panel is closed
     */
    closeComments: async function() {
        $("#toggle_comments").removeClass("expanded");

        this.isCommentsOpen = false;
        await this.repositionComments(true);
        this.unmountComments();

        // when closing comments, make sure to focus on document.body
        // again since it's possible that focus is stuck and preventing keyboard nav
        // from working - also, document.body.focus() doesn't work - this is an alternative
        document.activeElement?.blur();
    },

    /**
     * Repositions the comments panel depending on its current state (open/closed) and the window size
     */
    repositionComments: function(animate) {
        return new Promise(resolve => {
            this.$commentsContainer.velocity({
                translateX: this.isCommentsOpen ? [window.innerWidth - app.mainView.editorView.COMMENTS_BAR_WIDTH - 60, window.innerWidth - 60] : [window.innerWidth - 60, window.innerWidth - app.mainView.editorView.COMMENTS_BAR_WIDTH - 60]
            }, {
                duration: animate ? this.UI_ANIMATION_DURATION : 0,
                complete: () => resolve()
            });
        });
    },

    /**
     * Closes the comments panel (if open) and disposes the component
     */
    disposeComments: async function() {
        if (this.isCommentsOpen) {
            await this.closeComments();
        }
    },

    // ------------------------------------------------------------------------------------------------------------------------------------------------------------

    close: function() {
        // reset load state if it is stuck
        this.loadError = null;
        return new Promise(resolve => {
            this.hideUI(false);
            this.hideEditorMenuBar();
            this.hideSideBar();
            UIController.hideHelpPanel();

            this.disposeComments();

            // animate current slide
            let currentSlide = this.getCurrentSlide();
            if (currentSlide) {
                currentSlide.canvas.$el.css("transition", "opacity 500ms, transform 500ms");
                const scale = 0.5;
                const translateX = currentSlide.calculatedLayout ? currentSlide.calculatedLayout.translateX : 0;
                const translateY = currentSlide.calculatedLayout ? currentSlide.calculatedLayout.translateY : 0;
                currentSlide.canvas.$el.transform({
                    x: translateX + (this.CANVAS_WIDTH / 4 * (1 - scale)),
                    y: translateY + (this.CANVAS_HEIGHT / 4 * (1 - scale)),
                    scale: scale
                }).opacity(0);
            }

            // TODO: can we wait directly for the transition to complete?
            _.delay(() => {
                resolve();
            }, 300);
        });
    },

    remove: function() {
        // make sure we don't leave an orphaned side bar
        // (in case remove() was called w/o calling close() before)
        if (this.isShowingSideBar) {
            app.dialogManager.unregisterDialog(this.sideBar);
            this.sideBar.remove();
        }

        // clean up
        if (this.currentCanvas) {
            this.currentCanvas.removeAsCurrentCanvas();
            this.currentCanvas = null;
        }

        // remove dom event handlers
        $(document).off(".editor");
        $(window).off(".editor");

        // remove subviews
        this.selectionLayer.remove();
        if (this.slideOptions) {
            this.slideOptions.remove();
        }
        this.collaborators.remove();

        // cancel rendering
        this.canvasRenderer.cancelAll();

        // stop collaboration
        this.collaborators.stop();

        // Dispose presentation slides
        ds.selection.presentation.clearSlides();

        // clear global editor state
        ds.selection.presentation = null;
        ds.selection.slide = null;
        app.currentCanvas = null;

        // remove all canvases that the CanvasRenderer is managing
        this.canvasRenderer.removeAll();

        // remove dom event handlers
        $(window).off("resize");
        $(window).off("keydown");
        $(window).off("keypress");
        $(window).off("paste.editor");
        $(window).off("wheel");
        $("body").off("mousedown");

        this.$commentsContainer && this.$commentsContainer.remove();

        Backbone.View.prototype.remove.apply(this);
    }
});
